import React, { useMemo } from 'react';
import styles from './CustomPagination.module.css';

const CustomPagination = ({
  page = 1,
  limit = 10,
  total = 0,
  onPageChange,
  onLimitChange,
  showLimitSelector = false,
  limitOptions = [10, 25, 50, 100],
  siblingCount = 2,
  showFirstLast = true,
  showPrevNext = true,
  className = '',
  disabled = false,
  ...rest
}) => {
  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / limit));
  }, [total, limit]);

  // Generate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages = [];
    const showEllipsisStart = page > siblingCount + 2;
    const showEllipsisEnd = page < totalPages - siblingCount - 1;

    // Always show first page
    pages.push(1);

    // Show ellipsis or pages after first
    if (showEllipsisStart) {
      pages.push('ellipsis-start');
    } else {
      for (let i = 2; i <= Math.min(siblingCount + 2, totalPages - 1); i++) {
        if (!pages.includes(i)) pages.push(i);
      }
    }

    // Show pages around current page
    if (showEllipsisStart || showEllipsisEnd) {
      const start = Math.max(2, page - siblingCount);
      const end = Math.min(totalPages - 1, page + siblingCount);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i) && i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
    }

    // Show ellipsis or pages before last
    if (showEllipsisEnd) {
      if (!pages.includes('ellipsis-end')) {
        pages.push('ellipsis-end');
      }
    } else {
      for (let i = Math.max(2, totalPages - siblingCount - 1); i < totalPages; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
    }

    // Always show last page if more than 1 page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    // Sort and remove duplicates
    return pages.sort((a, b) => {
      if (typeof a === 'string') return a.includes('start') ? -1 : 1;
      if (typeof b === 'string') return b.includes('start') ? 1 : -1;
      return a - b;
    });
  }, [page, totalPages, siblingCount]);

  const handlePageChange = (newPage) => {
    if (disabled || newPage < 1 || newPage > totalPages || newPage === page) {
      return;
    }
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    if (onLimitChange) {
      onLimitChange(newLimit);
    }
  };

  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  if (total === 0) {
    return null;
  }

  return (
    <div className={`${styles.paginationWrapper} ${className}`} {...rest}>
      {showLimitSelector && (
        <div className={styles.limitSelector}>
          <span className={styles.limitLabel}>Show:</span>
          <select
            value={limit}
            onChange={handleLimitChange}
            className={styles.limitSelect}
            disabled={disabled}
          >
            {limitOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className={styles.limitLabel}>entries</span>
        </div>
      )}

      <nav className={styles.pagination} aria-label="Pagination">
        {/* Previous Button */}
        {showPrevNext && (
          <button
            type="button"
            className={`${styles.pageButton} ${styles.navButton}`}
            onClick={() => handlePageChange(page - 1)}
            disabled={disabled || isFirstPage}
            aria-label="Previous page"
          >
            «
          </button>
        )}

        {/* Page Numbers */}
        {pageNumbers.map((pageNum, index) => {
          if (typeof pageNum === 'string') {
            return (
              <span key={pageNum} className={styles.ellipsis}>
                ...
              </span>
            );
          }

          return (
            <button
              key={pageNum}
              type="button"
              className={`${styles.pageButton} ${
                page === pageNum ? styles.active : ''
              }`}
              onClick={() => handlePageChange(pageNum)}
              disabled={disabled}
              aria-label={`Page ${pageNum}`}
              aria-current={page === pageNum ? 'page' : undefined}
            >
              {pageNum}
            </button>
          );
        })}

        {/* Next Button */}
        {showPrevNext && (
          <button
            type="button"
            className={`${styles.pageButton} ${styles.navButton}`}
            onClick={() => handlePageChange(page + 1)}
            disabled={disabled || isLastPage}
            aria-label="Next page"
          >
            »
          </button>
        )}
      </nav>

      {/* Page Info */}
      <div className={styles.pageInfo}>
        <span>
          Showing {Math.min((page - 1) * limit + 1, total)} to{' '}
          {Math.min(page * limit, total)} of {total} entries
        </span>
      </div>
    </div>
  );
};

export default CustomPagination;
